import React from 'react';
import { Box, Button, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { LocateFixed, UserPlus, X } from 'lucide-react';
import { DefinitionList, Meter, PersonCell, SectionCard, StatusPill, useDelayedFlag } from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { TeamDot } from '../../../components/floorplan/teamColors';
import { FloorplanAssignment, FloorplanDesk, FloorplanLevel } from '../../../types/floorplan';
import { formatCount, formatNumber, formatPercent, MISSING } from '../../../utils/format';

interface SelectionPanelProps {
  desk: FloorplanDesk | null;
  /** The floor the selected desk is on (it may differ from the floor in view). */
  deskLevelName: string | null;
  assignment: FloorplanAssignment | null;
  pending: boolean;
  canAssign: boolean;
  canEditPlacement: boolean;
  level: FloorplanLevel | null;
  levelAssignedCount: number;
  unassignedPeopleCount: number;
  rosterCount: number;
  /**
   * The layout (floors, desks, assignments) hasn't loaded yet. Occupancy shows
   * skeletons instead of figures or a "choose a floor" prompt.
   */
  loading?: boolean;
  onOpenPerson: () => void;
  onAssignDesk: () => void;
  onShowOnFloor: () => void;
  onClearSelection: () => void;
}

const DeselectButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Tooltip title="Deselect desk">
    <IconButton size="small" aria-label="Deselect desk" onClick={onClick}>
      <X />
    </IconButton>
  </Tooltip>
);

/**
 * The right-rail record for the selected desk, or — with nothing selected —
 * the floor's occupancy at a glance.
 */
const SelectionPanel: React.FC<SelectionPanelProps> = ({
  desk,
  deskLevelName,
  assignment,
  pending,
  canAssign,
  canEditPlacement,
  level,
  levelAssignedCount,
  unassignedPeopleCount,
  rosterCount,
  loading = false,
  onOpenPerson,
  onAssignDesk,
  onShowOnFloor,
  onClearSelection,
}) => {
  const showSkeleton = useDelayedFlag(loading && !desk);

  if (!desk && loading) {
    // Same shape as the loaded card, so nothing jumps when the figures arrive.
    return (
      <SectionCard title="Occupancy" description="Select a desk to see who sits there." aria-label="Floor occupancy">
        <Box role="status" aria-label="Loading occupancy…">
          <span className="sbg-sr-only">Loading occupancy…</span>
          {/* Held but hidden for the first 300ms, so a quick load never flashes. */}
          <Stack spacing={2} aria-hidden="true" sx={{ visibility: showSkeleton ? 'visible' : 'hidden' }}>
            <Box>
              <Skeleton variant="rounded" width={88} height={32} />
              <Skeleton variant="rounded" width="72%" height={14} sx={{ mt: 1 }} />
              <Skeleton variant="rounded" height={4} sx={{ mt: 1.5 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pt: 2, borderTop: `1px solid ${sbg.border.subtle}` }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="rounded" width={96} height={14} />
                <Skeleton variant="rounded" width={128} height={12} sx={{ mt: 0.75 }} />
              </Box>
              <Skeleton variant="rounded" width={36} height={24} />
            </Box>
          </Stack>
        </Box>
      </SectionCard>
    );
  }

  if (!desk) {
    const deskCount = level?.desks.length ?? 0;
    const openCount = Math.max(deskCount - levelAssignedCount, 0);
    const share = deskCount ? (levelAssignedCount / deskCount) * 100 : null;
    return (
      <SectionCard
        title="Occupancy"
        description={level ? 'Select a desk to see who sits there.' : 'Choose a floor to see its desks.'}
        aria-label="Floor occupancy"
      >
        {level && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="numericLg" component="p" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                {deskCount ? formatPercent(share) : MISSING}
              </Typography>
              <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                {deskCount
                  ? `${formatNumber(levelAssignedCount)} of ${formatCount(deskCount, 'desk')} assigned · ${formatNumber(openCount)} open`
                  : 'No desks on this floor yet'}
              </Typography>
              {deskCount > 0 && (
                <Meter
                  value={levelAssignedCount}
                  max={deskCount}
                  aria-label={`${formatNumber(levelAssignedCount)} of ${formatCount(deskCount, 'desk')} assigned`}
                  sx={{ mt: 1.5 }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, pt: 2, borderTop: `1px solid ${sbg.border.subtle}` }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="labelSm" component="p" sx={{ color: sbg.text.primary }}>
                  Without a desk
                </Typography>
                <Typography variant="caption" component="p" sx={{ color: sbg.text.secondary }}>
                  {`Of ${formatCount(rosterCount, 'person', 'people')}, all floors`}
                </Typography>
              </Box>
              <Typography variant="numericMd" component="p" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                {formatNumber(unassignedPeopleCount)}
              </Typography>
            </Box>
          </Stack>
        )}
      </SectionCard>
    );
  }

  const title = `Desk ${desk.number}`;

  if (!assignment) {
    return (
      <SectionCard title={title} description={deskLevelName ?? undefined} action={<DeselectButton onClick={onClearSelection} />} aria-label={`${title} details`}>
        <Stack spacing={1.5} alignItems="flex-start">
          <StatusPill tone="neutral">{pending ? 'Saving' : 'Open'}</StatusPill>
          <Typography variant="body1" sx={{ color: sbg.text.secondary }}>
            {canAssign
              ? 'No one sits here yet. Drag a person from the roster onto this desk, or choose one.'
              : 'No one sits here yet.'}
          </Typography>
          {canAssign && (
            <Button variant="contained" size="small" startIcon={<UserPlus />} onClick={onAssignDesk} disabled={pending}>
              Assign employee
            </Button>
          )}
        </Stack>
      </SectionCard>
    );
  }

  const { employee } = assignment;

  return (
    <SectionCard
      title={title}
      description={deskLevelName ?? undefined}
      action={<DeselectButton onClick={onClearSelection} />}
      aria-label={`${title} details`}
      footer={
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%' }}>
          <Button variant="outlined" size="small" onClick={onOpenPerson}>
            {canEditPlacement ? 'Edit placement' : 'View details'}
          </Button>
          <Button variant="text" size="small" startIcon={<LocateFixed />} onClick={onShowOnFloor}>
            Show on floor
          </Button>
        </Box>
      }
    >
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
          <PersonCell name={employee.name} secondary={employee.email || undefined} size={40} />
          {pending ? (
            <StatusPill tone="info">Saving</StatusPill>
          ) : !employee.canEdit ? (
            <StatusPill tone="neutral">View only</StatusPill>
          ) : null}
        </Box>
        <DefinitionList
          items={[
            {
              label: 'Team',
              value: employee.teamName ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <TeamDot color={employee.teamColor} />
                  {employee.teamName}
                </Box>
              ) : MISSING,
            },
            { label: 'Department', value: employee.department?.name || MISSING },
            { label: 'Manager', value: employee.managerName || MISSING },
          ]}
        />
      </Stack>
    </SectionCard>
  );
};

export default SelectionPanel;
